// navigation-completion — "apply the organisation navigation policy to this
// submission" controls, shown in the Inspector → Navigation header.
//
// Why this exists: Settings only sets the ORGANISATION policy. The navigation
// editor is correctly read-only until the submission captures a snapshot of
// that policy (an absent or stale snapshot never authorises mutation). This is
// the authorised UI path to capture it. It is deliberately concise and
// jargon-free: it names the exact version and reports one of four plain states
// (current / needs-applying / out-of-date / validate-only), never hashes.
//
// Contracts it preserves:
//   • The button is enabled only with the real Submissions:write permission;
//     the server re-checks every call. Unknown permissions (a failed /users/me)
//     show an unknown state, never a false "ask an administrator".
//   • A reason is mandatory and the exact version is applied (optimistic).
//   • A 409 (the org policy moved under the user) shows the fresh state and asks
//     the user to apply again — never an automatic retry.
//   • It never auto-enables an inactive (validate-only) organisation policy; it
//     links to Settings instead.
//   • Refreshing an out-of-date submission is explicit, and the copy states that
//     existing generated/edited drafts stay bound and may need regeneration.

import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { ApiError, parseApiErrorBody } from '@/api/v2Client';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import {
  navigationSnapshot,
  type NavigationSnapshotStateWire,
} from '@/api/navigationSnapshot';
import { navStyles } from './navShared';

export interface NavigationSnapshotControlsProps {
  readonly submissionId: string;
  /** Refresh the navigation envelope after a successful apply (editor unlocks). */
  readonly onCaptured?: () => void;
}

/** The subset of the 409 body we read back to re-sync the on-screen state. */
function stateFromErrorBody(body: unknown): NavigationSnapshotStateWire | null {
  if (body === null || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  if (typeof b['status'] !== 'string' || typeof b['currentPolicyVersion'] !== 'number') return null;
  return {
    ok: true,
    status: b['status'] as NavigationSnapshotStateWire['status'],
    currentPolicyVersion: b['currentPolicyVersion'] as number,
    currentPolicyActivated: b['currentPolicyActivated'] === true,
    currentPreset: typeof b['currentPreset'] === 'string' ? (b['currentPreset'] as string) : '',
    authorizes: b['authorizes'] === true,
    snapshot: (b['snapshot'] ?? null) as NavigationSnapshotStateWire['snapshot'],
  };
}

export function NavigationSnapshotControls({
  submissionId,
  onCaptured,
}: NavigationSnapshotControlsProps): ReactElement {
  const perms = useUserPermissions();
  const [state, setState] = useState<NavigationSnapshotStateWire | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);

  const fetchState = useCallback(async (): Promise<void> => {
    setLoading(true);
    setLoadError(null);
    try {
      setState(await navigationSnapshot.get(submissionId));
    } catch (err) {
      const body = parseApiErrorBody(err);
      setLoadError(body?.message ?? body?.error ?? (err instanceof Error ? err.message : String(err)));
    } finally {
      setLoading(false);
    }
  }, [submissionId]);

  useEffect(() => {
    setState(null);
    setReason('');
    setActionError(null);
    setConflict(null);
    void fetchState();
  }, [fetchState]);

  const apply = useCallback(async (): Promise<void> => {
    if (!state) return;
    const trimmed = reason.trim();
    if (trimmed.length === 0) {
      setActionError('Enter a reason before applying the organisation navigation policy.');
      return;
    }
    setBusy(true);
    setActionError(null);
    setConflict(null);
    try {
      const next = await navigationSnapshot.capture(submissionId, {
        expectedPolicyVersion: state.currentPolicyVersion,
        reason: trimmed,
      });
      setState(next);
      setReason('');
      onCaptured?.();
    } catch (err) {
      const status = err instanceof ApiError ? err.status : 0;
      const body = parseApiErrorBody(err);
      if (status === 409) {
        // The org policy moved under us — resync to the fresh version the server
        // returned and ask the user to apply again. NO automatic retry.
        const fresh = stateFromErrorBody(body);
        if (fresh) setState(fresh);
        setConflict(
          `${body?.message ?? 'The organisation navigation policy changed since you loaded it.'} ` +
            'Review the current version and apply it again — nothing was applied automatically.',
        );
      } else {
        setActionError(body?.message ?? body?.error ?? (err instanceof Error ? err.message : String(err)));
      }
    } finally {
      setBusy(false);
    }
  }, [state, reason, submissionId, onCaptured]);

  if (loading && !state) {
    return (
      <div style={navStyles.muted} data-testid="nav-snapshot-loading">
        Checking the submission navigation policy…
      </div>
    );
  }
  if (loadError && !state) {
    return (
      <div style={navStyles.error} role="alert" data-testid="nav-snapshot-load-error">
        Could not load the submission navigation policy: {loadError}{' '}
        <button type="button" style={navStyles.smallBtn} data-testid="nav-snapshot-retry" onClick={() => void fetchState()}>
          Retry
        </button>
      </div>
    );
  }
  if (!state) return <div data-testid="nav-snapshot-empty" style={navStyles.muted} />;

  // Unknown permissions (a /users/me outage) must NOT read as "denied".
  const permsUnknown = perms.loading || perms.failed;
  const canWrite = !permsUnknown && perms.hasPermission('Submissions', 'write');
  const v = state.currentPolicyVersion;

  const statusText: Record<NavigationSnapshotStateWire['status'], string> = {
    current: `This submission is using the current organisation navigation policy (version ${v}).`,
    missing:
      'This submission has not applied the organisation navigation policy yet. ' +
      'The navigation editor stays read-only until it is applied.',
    stale:
      `This submission is using an earlier organisation navigation policy ` +
      `(version ${state.snapshot?.capturedPolicyVersion ?? '—'}); the organisation policy is now version ${v}. ` +
      'Refresh to apply the current policy. Existing generated or edited drafts stay as they are and may ' +
      'need to be regenerated against the refreshed policy.',
    inactive:
      `The organisation navigation policy is currently validate-only (version ${v}), ` +
      'so no automatic navigation changes are authorised for this submission.',
  };

  const showApply = state.status === 'missing';
  const showRefresh = state.status === 'stale';
  const needsAction = showApply || showRefresh;

  return (
    <section style={navStyles.section} data-testid="nav-snapshot">
      <h4 style={navStyles.heading}>Submission navigation policy</h4>
      <div style={navStyles.value} data-testid={`nav-snapshot-status-${state.status}`}>
        {statusText[state.status]}
      </div>

      {!state.currentPolicyActivated && (
        <div style={navStyles.note} data-testid="nav-snapshot-inactive-note">
          The organisation navigation policy is not activated. Activate it in{' '}
          <a href="/admin/settings" data-testid="nav-snapshot-settings-link">
            Settings → navigation policy
          </a>{' '}
          to enable automatic navigation; applying it here will not turn it on.
        </div>
      )}

      {needsAction && !canWrite && (
        <div style={navStyles.note} data-testid="nav-snapshot-readonly-note">
          {permsUnknown
            ? 'Your permissions could not be confirmed. Reload to try again.'
            : 'You do not have permission to apply the organisation navigation policy. Ask an administrator with submission-write access.'}
        </div>
      )}

      {needsAction && canWrite && (
        <div style={{ marginTop: 8 }}>
          <label style={navStyles.label} htmlFor="nav-snapshot-reason">
            Reason (required — recorded in the audit trail)
          </label>
          <textarea
            id="nav-snapshot-reason"
            data-testid="nav-snapshot-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Why the organisation navigation policy is being applied to this submission"
            style={{ ...navStyles.input, width: '100%', marginTop: 4, resize: 'vertical' }}
          />
          <div style={{ marginTop: 8 }}>
            <button
              type="button"
              data-testid="nav-snapshot-apply"
              disabled={busy}
              aria-disabled={busy}
              style={busy ? navStyles.buttonDisabled : navStyles.button}
              onClick={() => void apply()}
            >
              {busy
                ? 'Applying…'
                : showRefresh
                  ? `Refresh submission policy (version ${v})`
                  : `Apply current navigation policy to this submission (version ${v})`}
            </button>
          </div>
        </div>
      )}

      {conflict && (
        <div style={{ ...navStyles.note, color: 'var(--danger, #b91c1c)' }} role="alert" data-testid="nav-snapshot-conflict">
          {conflict}
        </div>
      )}
      {actionError && (
        <div style={{ ...navStyles.error, marginTop: 6 }} role="alert" data-testid="nav-snapshot-action-error">
          {actionError}
        </div>
      )}
    </section>
  );
}

export default NavigationSnapshotControls;
